import { useTimerStore } from '@/stores/timer-store';
import { Timer } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';

/** Formats seconds into HH:MM:SS. */
function formatTimer(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

/**
 * Small inline timer indicator for the dashboard top bar.
 * Shows a live ticking clock while a timer is running, with the task name.
 * Clicking navigates to the time-tracking page.
 */
export function TimerIndicator() {
  const isRunning = useTimerStore((s) => s.isRunning);
  const elapsedSeconds = useTimerStore((s) => s.elapsedSeconds);
  const taskTitle = useTimerStore((s) => s.taskTitle);
  const navigate = useNavigate();

  if (!isRunning) return null;

  return (
    <button
      onClick={() => navigate('/time')}
      className={cn(
        'flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-medium',
        'bg-dyndev-green/10 text-dyndev-green border border-dyndev-green/20',
        'hover:bg-dyndev-green/20 transition-colors animate-pulse',
        'max-w-[240px]'
      )}
      title={`Timing: ${taskTitle || 'Unknown task'}`}
    >
      <Timer size={12} className="flex-shrink-0" />
      <span className="font-mono tabular-nums flex-shrink-0">
        {formatTimer(elapsedSeconds)}
      </span>
      <span className="truncate text-[var(--text-secondary)] hidden sm:inline">
        {taskTitle || 'Timing…'}
      </span>
    </button>
  );
}
